import i18n from '@/i18n';
/**
 * 商品库条目 → TEMU 发品表单（商品名称 / 英文名称 / 主图 / 细节图 / 货号拼印花名）。
 * 商家 SKU 不写入印花名；印花图仅站内展示，不上传 Temu。
 */

import {
  carouselUrlsForListingItem,
  detailImageUrlsForListingItem,
  printImageUrlsForListingItem,
  printImageNamesForListingItem,
  appendPrintNamesAfterHyphen,
  isGenericListingImageName,
  isOpaqueStorageBaseName,
  SHEIN_SUPPLIER_CODE_MAX_LEN,
} from './platformListingItemMedia';
import { resolveListingItemDisplayTitle } from './preferDownstreamListingAssets';
import { isManagedStorageUrl, resolveFetchableImageUrl, stripImageFileExtension } from '@/utils/imageUrlUtils';
import { toManagedCanonicalUploadsUrl } from '@/utils/imageDisplayAccess';
import { findImageSpaceRecordByUrl } from './imageCardMetaActions';
import { convertListingImageToTemu } from './convertListingImageToTemu';
import {
  ensureEnglishListingTitle,
  isLikelyEnglishListingTitle,
  titleContainsCjk,
  TEMU_ENGLISH_TITLE_FAIL_MESSAGE,
} from './ensureEnglishListingTitle';
import { ensureChineseListingTitle } from './ensureChineseListingTitle';
import {
  resolveListingSlotSkcIndex,
  extractColorFromImageNames,
  extractColorCodeFromSupplierSku,
  formatUnmatchedListingColorMessage,
} from './listingPatternColorMatch';
import { skuSizeKey } from '@/temu-platform/utils/temuSizeChart';
import {
  EMPTY_TEMU_PRINT_INFRINGEMENT,
  normalizeTemuPrintInfringement,
} from '@/temu-platform/utils/temuPublishPrintInfringement';
import {
  TEMU_MAX_DETAIL_IMAGES,
} from '@/temu-platform/utils/temuProductImage';
import {
  deriveTemuMaterialFromMainSlot,
  formHasTemuMaterialImage,
} from '@/temu-platform/utils/temuMaterialFromMain';
import { getListingProductVideo } from '@/pages/ipe/workbench/utils/listingProductVideo';
import { videoSpaceRecordToUploadFile } from '@/pages/ipe/workbench/utils/videoSpaceLibraryPick';
import { temuPlatformAPI } from '@/utils/api';
import { getProductMeta, hydrateProductMetaFromItemRaw, resolveListingColorSlots } from './platformListingProductMeta';

/** TEMU 货号拼接印花名后上限（与 validateTemuPublishForm 一致） */
export const TEMU_SUPPLIER_CODE_MAX_LEN = SHEIN_SUPPLIER_CODE_MAX_LEN;

/** 细节图槽位上限（主图 1 + 细节 ≤9，轮播合计 ≤10） */
const MAX_DETAIL_IMAGES = TEMU_MAX_DETAIL_IMAGES;

export const LISTING_NO_PUBLISHABLE_COLOR_MESSAGE =
  '没有可推送的颜色：请确保已接收颜色能匹配模板主规格，且该颜色含主图';

/** 商品库行 → TEMU 发品表单印花侵权检测快照 */
export function resolveListingPrintInfringementForTemu(listingItem) {
  if (!listingItem || listingItem.id == null) {
    return { ...EMPTY_TEMU_PRINT_INFRINGEMENT };
  }
  hydrateProductMetaFromItemRaw(listingItem);
  const meta = listingItem.id != null ? (getProductMeta(listingItem.id) || {}) : {};
  const raw = listingItem?.raw && typeof listingItem.raw === 'object' ? listingItem.raw : {};
  const rawRisk = String(raw?.risk || '').trim().toLowerCase();
  const effectiveRisk = (meta.risk && meta.risk !== 'pending')
    ? meta.risk
    : (rawRisk && rawRisk !== 'pending' ? rawRisk : meta.risk);
  const rawAdvice = String(raw?.riskAdvice || '').trim();
  const rawElements = Array.isArray(raw?.riskElements)
    ? raw.riskElements.map((el) => String(el || '').trim()).filter(Boolean)
    : [];
  const effectiveAdvice = meta.riskAdvice
    || rawAdvice
    || (rawElements.length ? rawElements.join('、') : '');
  const effectiveAt = String(meta.assessedAt || raw?.assessedAt || '').trim();
  const assessing = Boolean(meta.assessing);
  const hasResult = Boolean(effectiveAdvice)
    || (effectiveRisk && effectiveRisk !== 'pending' && !assessing);
  if (!hasResult) {
    return { ...EMPTY_TEMU_PRINT_INFRINGEMENT };
  }
  return normalizeTemuPrintInfringement({
    risk: effectiveRisk || 'pending',
    assessing,
    riskAdvice: effectiveAdvice,
    assessedAt: effectiveAt,
  });
}

function toAbsoluteUrl(url) {
  const u = String(url || '').trim();
  if (!u) return '';
  if (/^(data:|blob:|https?:)/i.test(u)) return u;
  if (u.startsWith('//')) return `https:${u}`;
  if (typeof window !== 'undefined' && u.startsWith('/')) {
    return `${window.location.origin}${u}`;
  }
  return u;
}

function fileNameFromUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const path = new URL(raw, 'https://local.invalid').pathname;
    return decodeURIComponent((path.split('/').pop() || '').split('?')[0]);
  } catch {
    return (raw.split('/').pop() || '').split('?')[0];
  }
}

function readablePrintName(...candidates) {
  for (const raw of candidates) {
    const name = stripImageFileExtension(raw);
    if (name && !isOpaqueStorageBaseName(name) && !isGenericListingImageName(name)) return name;
  }
  return '';
}

async function resolvePrintDisplayName(url, nameHint = '', index = 0) {
  const hinted = readablePrintName(nameHint);
  if (hinted) return hinted;
  const fromUrl = readablePrintName(fileNameFromUrl(url));
  if (fromUrl) return fromUrl;
  try {
    const record = await findImageSpaceRecordByUrl(url);
    const fromSpace = readablePrintName(record?.name, record?.fileName, record?.title);
    if (fromSpace) return fromSpace;
  } catch {
    /* 图片空间回表失败时用占位名 */
  }
  return i18n.t('workbench:ipeUtilError.stampLabel', { index: index + 1 });
}

/** 印花图不传 TEMU：只留站内地址和名称拼货号。 */
function printImageStub(url, nameHint = '', index = 0) {
  const absolute = toAbsoluteUrl(url);
  if (!absolute) return null;
  const canonical = toManagedCanonicalUploadsUrl(absolute);
  const stored = canonical || absolute;
  const name = readablePrintName(nameHint, fileNameFromUrl(stored)) || `印花${index + 1}`;
  return {
    id: `listing-print-${index}`,
    name,
    previewUrl: stored,
    publicUrl: stored,
    ipeUrl: stored,
    url: stored,
    temuUrl: '',
    needsSignedPreview: Boolean(canonical) || isManagedStorageUrl(stored),
  };
}

async function imageFromUrl(url, sortHint = 0, nameHint = '', {
  shopId = '',
  imageType = 0,
} = {}) {
  const absolute = toAbsoluteUrl(url);
  if (!absolute) return null;
  const canonical = toManagedCanonicalUploadsUrl(absolute);
  const stored = canonical || absolute;
  let previewUrl = stored;
  if (!stored.startsWith('data:') && !stored.startsWith('blob:') && !canonical) {
    try {
      const fetchable = await resolveFetchableImageUrl(stored);
      if (fetchable) previewUrl = fetchable;
    } catch {
      /* 保留原地址 */
    }
  }
  const name = readablePrintName(nameHint, fileNameFromUrl(stored))
    || stripImageFileExtension(nameHint)
    || stripImageFileExtension(fileNameFromUrl(stored))
    || '';
  let temuUrl = '';
  let convertedIpe = stored;
  let convertedPublic = stored;
  if (shopId && imageType > 0) {
    try {
      const converted = await convertListingImageToTemu({
        shopId,
        url: stored,
        imageType,
        nameHint: name,
      });
      if (converted?.temuUrl) {
        temuUrl = converted.temuUrl;
        convertedIpe = converted.ipeUrl || stored;
        convertedPublic = converted.publicUrl || convertedIpe;
        previewUrl = converted.previewUrl || previewUrl;
      }
    } catch {
      /* 转换失败仍保留站内地址 */
    }
  }
  return {
    id: `listing-img-${Date.now()}-${sortHint}-${Math.random().toString(36).slice(2, 7)}`,
    name,
    previewUrl,
    publicUrl: convertedPublic,
    ipeUrl: convertedIpe,
    url: temuUrl || convertedIpe,
    temuUrl,
    needsSignedPreview: Boolean(canonical) || isManagedStorageUrl(convertedIpe),
  };
}

function listingColorSlots(listingItem) {
  const slots = resolveListingColorSlots(listingItem);
  return (Array.isArray(slots) ? slots : [])
    .map((slot) => ({
      patternName: String(slot?.patternName || slot?.mainName || '').trim(),
      mainUrl: String(slot?.mainUrl || slot?.main_url || '').trim(),
      mainName: String(slot?.mainName || slot?.patternName || slot?.main_name || '').trim(),
      colorCode: String(slot?.colorCode || slot?.color_code || '').trim(),
      colorLabel: String(slot?.colorLabel || slot?.color_label || '').trim(),
      detailUrls: Array.isArray(slot?.detailUrls)
        ? slot.detailUrls.map((u) => String(u || '').trim()).filter(Boolean)
        : (Array.isArray(slot?.detail_urls)
          ? slot.detail_urls.map((u) => String(u || '').trim()).filter(Boolean)
          : []),
      detailNames: Array.isArray(slot?.detailNames)
        ? slot.detailNames.map((n) => String(n || '').trim())
        : (Array.isArray(slot?.detail_names) ? slot.detail_names.map((n) => String(n || '').trim()) : []),
    }))
    .filter((slot) => slot.mainUrl);
}

function clearSkcPublishImages(skc) {
  return {
    ...skc,
    mainImage: null,
    detailImages: [],
    squareImage: null,
    colorBlockImage: null,
    showroomSlots: {},
  };
}

/** 清空前快照模板主图和细节图：缺主图时用模板主图兜底，细节图始终接在创作图后面。 */
function snapshotTemplateSkcMedia(skc) {
  return {
    mainImage: skc?.mainImage || null,
    detailImages: Array.isArray(skc?.detailImages) ? skc.detailImages.filter(Boolean) : [],
  };
}

function imageIdentityKey(img) {
  const url = skcImageUrl(img);
  return url ? url.toLowerCase() : '';
}

/**
 * 商品库色槽图优先。主图缺失时用模板同色主图兜底。
 * 模板细节图接在创作细节图后面，同 URL 不重复。
 */
async function supplementSkcMediaFromTemplate(skc, templateMedia, sortBase, convertOpts) {
  if (!skc || !templateMedia) return skc;
  let next = skc;
  if (!skcImageUrl(skc.mainImage) && skcImageUrl(templateMedia.mainImage)) {
    const url = skcImageUrl(templateMedia.mainImage);
    const mainImage = String(templateMedia.mainImage?.temuUrl || '').trim()
      ? { ...templateMedia.mainImage }
      : await imageFromUrl(
        url,
        sortBase,
        templateMedia.mainImage?.name || '',
        convertOpts(1),
      );
    if (mainImage && imageIdentityKey(mainImage)) {
      next = {
        ...next,
        mainImage,
        squareImage: null,
        colorBlockImage: null,
      };
    }
  }

  const mergedDetails = Array.isArray(next.detailImages) ? [...next.detailImages] : [];
  const seen = new Set(mergedDetails.map((img) => imageIdentityKey(img)).filter(Boolean));
  (templateMedia.detailImages || []).forEach((img) => {
    if (mergedDetails.length >= MAX_DETAIL_IMAGES) return;
    const key = imageIdentityKey(img);
    if (!key || seen.has(key)) return;
    seen.add(key);
    mergedDetails.push(img);
  });
  return {
    ...next,
    detailImages: mergedDetails.slice(0, MAX_DETAIL_IMAGES),
  };
}

/** 仅货号拼印花名，不改商家 SKU。 */
function appendPrintNamesToSkcSupplierCode(skc, printNames) {
  if (!printNames.length) return skc;
  return {
    ...skc,
    supplierCode: appendPrintNamesAfterHyphen(
      skc?.supplierCode,
      printNames,
      { maxLen: TEMU_SUPPLIER_CODE_MAX_LEN },
    ),
  };
}

function supplierCodeWithPrintNames(skc, printNames) {
  const names = (Array.isArray(printNames) ? printNames : [])
    .map((name) => String(name || '').trim())
    .filter(Boolean);
  const pattern = names.join('+');
  const colorCode = String(skc?.colorCode || '').trim();
  const raw = String(skc?.supplierCode || '').trim().replace(/-+$/, '');
  let spu = '';
  if (colorCode && raw) {
    const marker = `-${colorCode}`;
    let idx = raw.lastIndexOf(marker);
    while (idx > 0) {
      const after = raw.slice(idx + marker.length);
      if (!after || after.startsWith('-')) {
        spu = raw.slice(0, idx);
        break;
      }
      idx = raw.lastIndexOf(marker, idx - 1);
    }
  }
  if (spu && colorCode) {
    const next = pattern ? `${spu}-${colorCode}-${pattern}` : `${spu}-${colorCode}-`;
    return next.slice(0, TEMU_SUPPLIER_CODE_MAX_LEN);
  }
  if (!names.length) return String(skc?.supplierCode || '');
  return appendPrintNamesAfterHyphen(skc?.supplierCode, names, {
    maxLen: TEMU_SUPPLIER_CODE_MAX_LEN,
  });
}

/**
 * 上传印花图后把图案编号拼进货号（SPU-颜色-图案）。不改商家 SKU。
 * 清空印花图时，有颜色编码则把货号收回「SPU-颜色-」。
 */
export function applyPrintNamesToTemuSkcList(skcList, printNames) {
  return (Array.isArray(skcList) ? skcList : []).map((skc) => {
    if (!skc || typeof skc !== 'object') return skc;
    const supplierCode = supplierCodeWithPrintNames(skc, printNames);
    if (supplierCode === skc.supplierCode) return skc;
    return { ...skc, supplierCode };
  });
}

/**
 * 从发品表单印花图槽位提取拼接用名称（去扩展名）。
 * @param {object[]} images
 * @returns {string[]}
 */
export function printNamesFromTemuPrintImages(images) {
  return (Array.isArray(images) ? images : [])
    .map((img, index) => {
      const fromName = readablePrintName(img?.name);
      if (fromName) return fromName;
      const fromUrl = readablePrintName(fileNameFromUrl(
        img?.ipeUrl || img?.publicUrl || img?.url || img?.previewUrl || img?.temuUrl || '',
      ));
      return fromUrl || `印花${index + 1}`;
    })
    .filter(Boolean);
}

/**
 * 提交前：弱名（哈希）回表图片空间展示名，再写回 printImages / ipePrintImageNames。
 * @param {object} form
 * @returns {Promise<object>}
 */
export async function enrichTemuFormPrintNames(form) {
  if (!form || typeof form !== 'object') return form;
  const images = Array.isArray(form.printImages) ? form.printImages : [];
  if (!images.length) return form;
  const nextImages = await Promise.all(images.map(async (img, index) => {
    if (!img || typeof img !== 'object') return img;
    const current = readablePrintName(img.name);
    if (current) return { ...img, name: current };
    const url = img.ipeUrl || img.publicUrl || img.url || img.previewUrl || '';
    const name = await resolvePrintDisplayName(url, '', index);
    return { ...img, name };
  }));
  const names = printNamesFromTemuPrintImages(nextImages);
  return {
    ...form,
    printImages: nextImages,
    ipePrintImageNames: names,
  };
}

function skcImageUrl(image) {
  if (!image) return '';
  return String(
    image.temuUrl
    || image.publicUrl
    || image.previewUrl
    || image.ipeUrl
    || image.url
    || '',
  ).trim();
}

/**
 * 刊登推送：有主图即可进入自动规则。模板细节图在组包时保留。
 * @param {object} skc
 * @returns {boolean}
 */
export function skcHasPlatformPublishImages(skc) {
  return Boolean(skcImageUrl(skc?.mainImage));
}

/**
 * @param {object[]} skcList
 * @returns {object[]}
 */
export function filterSkcsReadyForPlatformPublish(skcList) {
  return (Array.isArray(skcList) ? skcList : []).filter(skcHasPlatformPublishImages);
}

function listingColorSlotHasPublishImages(slot) {
  return Boolean(String(slot?.mainUrl || '').trim());
}

function ensureSkcList(form) {
  const list = Array.isArray(form?.skcList) ? [...form.skcList] : [];
  if (list.length === 0) {
    list.push({
      id: `skc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      colorAttributeId: '',
      colorValueId: '',
      colorLabel: '',
      colorIsCustom: false,
      colorExtraValue: '',
      supplierCode: '',
      skcName: '',
      mainImage: null,
      showroomSlots: {},
      detailImages: [],
      squareImage: null,
      colorBlockImage: null,
      skuRows: [],
    });
  }
  return list;
}

function deriveSelectedSizeIds(skcList = []) {
  const ids = [];
  const seen = new Set();
  (skcList || []).forEach((skc) => {
    (skc.skuRows || []).forEach((sku) => {
      const key = skuSizeKey(sku);
      if (key && !seen.has(key)) {
        seen.add(key);
        ids.push(key);
      }
    });
  });
  return ids;
}

/** 印花/图片文件名集合：禁止把「13」这类印花名当成商品标题。 */
function listingPrintNameSet(listingItem) {
  const names = printImageNamesForListingItem(listingItem);
  const set = new Set(
    (Array.isArray(names) ? names : [])
      .map((n) => stripImageFileExtension(n).toLowerCase())
      .filter(Boolean),
  );
  (Array.isArray(listingItem?.printImageNames) ? listingItem.printImageNames : []).forEach((n) => {
    const key = stripImageFileExtension(n).toLowerCase();
    if (key) set.add(key);
  });
  return set;
}

function isListingPrintNameTitle(text, listingItem) {
  const key = stripImageFileExtension(text).toLowerCase();
  if (!key) return false;
  return listingPrintNameSet(listingItem).has(key);
}

/** 从商品库行里挑出一段含汉字的标题（展示覆盖 / 库内字段）。不含印花名 / 图片 name。 */
export function pickChineseListingTitleCandidate(listingItem) {
  const candidates = [
    resolveListingItemDisplayTitle(listingItem),
    listingItem?.preferredTitle,
    listingItem?.title,
    listingItem?.productName,
  ];
  for (const raw of candidates) {
    const text = String(raw || '').trim();
    if (!text || !titleContainsCjk(text)) continue;
    if (isListingPrintNameTitle(text, listingItem)) continue;
    return text;
  }
  return '';
}

/** 从商品库行里挑出一段纯英文标题。跳过印花名（如「13」）误写入的 title。 */
export function pickEnglishListingTitleCandidate(listingItem) {
  const candidates = [
    listingItem?.titleEn,
    listingItem?.englishTitle,
    listingItem?.title,
    resolveListingItemDisplayTitle(listingItem),
  ];
  for (const raw of candidates) {
    const text = String(raw || '').trim();
    if (!text || titleContainsCjk(text) || !isLikelyEnglishListingTitle(text)) continue;
    if (isListingPrintNameTitle(text, listingItem)) continue;
    return text;
  }
  return '';
}

/**
 * 商品库标题 → TEMU 商品名称（中文）+ 英文名称。
 * 中文标题：商品名称直填，英文名称译英；英文标题：商品名称译中，英文名称直填。
 * @param {string} sourceTitle
 * @param {{ chineseFallback?: string, englishFallback?: string }} [options]
 */
export async function resolveTemuListingTitles(sourceTitle, options = {}) {
  const text = String(sourceTitle || '').trim();
  const chineseFallback = String(options.chineseFallback || '').trim();
  const englishFallback = String(options.englishFallback || '').trim();
  const zh = titleContainsCjk(text)
    ? text
    : (titleContainsCjk(chineseFallback) ? chineseFallback : '');
  const en = (!titleContainsCjk(text) && isLikelyEnglishListingTitle(text))
    ? text
    : ((!titleContainsCjk(englishFallback) && isLikelyEnglishListingTitle(englishFallback))
      ? englishFallback
      : '');

  // 只要有中文候选：商品名称固定用中文，不再因英→中失败而整单失败
  if (zh) {
    const titleEn = en || await ensureEnglishListingTitle(zh, {
      failMessage: TEMU_ENGLISH_TITLE_FAIL_MESSAGE,
    });
    return { title: zh, titleEn };
  }
  if (!text && !en) {
    return { title: '', titleEn: '' };
  }
  if (en) {
    const title = await ensureChineseListingTitle(en, {
      fallbackChinese: chineseFallback,
    });
    return { title, titleEn: en };
  }
  if (isLikelyEnglishListingTitle(text)) {
    const title = await ensureChineseListingTitle(text, {
      fallbackChinese: chineseFallback,
    });
    return { title, titleEn: text };
  }
  // 货号类：两边都用原文，英文名可后续组包时再翻
  return { title: text, titleEn: text };
}

/**
 * 组包/自动上架前：按字段语种写入标题。
 * - 商品名称（中文栏）：英文则译中，中文直填
 * - 英文名称：中文则译英，英文直填
 * 禁止把中文直接写入英文名称，或把英文直接写入中文商品名称。
 * @param {object} form
 * @param {{ chineseFallback?: string }} [options]
 *   chineseFallback：商品库展示标题等；当 form.title 被模板误写成 ID（如「9」）时用来兜底
 */
export async function ensureTemuFormTitlesLocalized(form, options = {}) {
  if (!form || typeof form !== 'object') return form;
  const hintChinese = String(options.chineseFallback || '').trim();
  const rawTitle = String(form.title || form.productName || '').trim();
  const rawTitleEn = String(form.titleEn || form.englishTitle || '').trim();
  if (!rawTitle && !rawTitleEn && !hintChinese) return form;

  // 表单标题非中文（空 / 纯数字 ID / 英文）时，优先用商品库中文标题
  let title = titleContainsCjk(rawTitle)
    ? rawTitle
    : (titleContainsCjk(hintChinese) ? hintChinese : rawTitle);
  let titleEn = rawTitleEn;
  const chineseFallback = titleContainsCjk(title)
    ? title
    : (titleContainsCjk(rawTitle)
      ? rawTitle
      : (titleContainsCjk(hintChinese)
        ? hintChinese
        : (titleContainsCjk(rawTitleEn) ? rawTitleEn : '')));

  // 1) 中文商品名称：英文则译中，中文直填；译失败可用另一字段中文兜底
  if (title) {
    if (!titleContainsCjk(title) && isLikelyEnglishListingTitle(title)) {
      title = await ensureChineseListingTitle(title, { fallbackChinese: chineseFallback });
    } else if (!titleContainsCjk(title) && titleContainsCjk(chineseFallback)) {
      // 货号/纯数字等：有中文兜底则直接用中文，避免把「9」当成商品名
      title = chineseFallback;
    }
  } else if (titleEn) {
    title = titleContainsCjk(titleEn)
      ? titleEn
      : (titleContainsCjk(chineseFallback)
        ? chineseFallback
        : await ensureChineseListingTitle(titleEn, { fallbackChinese: chineseFallback }));
  } else if (titleContainsCjk(chineseFallback)) {
    title = chineseFallback;
  }

  // 2) 英文名称：已是英文则保留；原文为英文时优先用原文，避免中→英再译失真
  const titleEnOk = Boolean(
    titleEn
    && !titleContainsCjk(titleEn)
    && isLikelyEnglishListingTitle(titleEn),
  );
  if (!titleEnOk) {
    if (rawTitle && !titleContainsCjk(rawTitle) && isLikelyEnglishListingTitle(rawTitle)) {
      titleEn = rawTitle;
    } else if (rawTitleEn && !titleContainsCjk(rawTitleEn) && isLikelyEnglishListingTitle(rawTitleEn)) {
      titleEn = rawTitleEn;
    } else {
      const enSource = titleContainsCjk(title)
        ? title
        : (titleContainsCjk(rawTitleEn) ? rawTitleEn : (title || rawTitleEn));
      titleEn = await ensureEnglishListingTitle(enSource, {
        failMessage: TEMU_ENGLISH_TITLE_FAIL_MESSAGE,
      });
    }
  }

  if (titleContainsCjk(titleEn)) {
    titleEn = await ensureEnglishListingTitle(title || titleEn, {
      failMessage: TEMU_ENGLISH_TITLE_FAIL_MESSAGE,
    });
  }

  return {
    ...form,
    title,
    productName: title,
    titleEn,
    englishTitle: titleEn,
  };
}

/**
 * @param {object} templateForm 商品模板 form 快照
 * @param {object} listingItem 商品库行
 * @param {object} [categoryMeta] { categoryId, productTypeId, categoryPathLabel }
 * @param {{ shopId?: string|number }} [options]
 * @returns {Promise<object>} 合并后的发品 form
 */
export async function applyListingItemToTemuPublishForm(
  templateForm,
  listingItem,
  categoryMeta = {},
  options = {},
) {
  const shopId = String(options.shopId || '').trim();
  const convertOpts = (imageType) => (shopId ? { shopId, imageType } : {});
  const base = templateForm && typeof templateForm === 'object' ? { ...templateForm } : {};
  const chineseFallback = pickChineseListingTitleCandidate(listingItem);
  const englishFallback = pickEnglishListingTitleCandidate(listingItem);
  const displayTitle = String(
    resolveListingItemDisplayTitle(listingItem) || listingItem?.title || '',
  ).trim();
  const sourceTitle = String(
    chineseFallback
      || englishFallback
      || (displayTitle && !isListingPrintNameTitle(displayTitle, listingItem) ? displayTitle : '')
      || '',
  ).trim();
  const { title: resolvedTitle, titleEn: resolvedTitleEn } = (sourceTitle || chineseFallback || englishFallback)
    ? await resolveTemuListingTitles(sourceTitle, {
      chineseFallback,
      englishFallback,
    })
    : { title: '', titleEn: '' };
  const templateTitle = String(base.title || base.productName || '').trim();
  const templateTitleEn = String(base.titleEn || base.englishTitle || '').trim();
  const finalTitle = String(resolvedTitle || '').trim()
    || chineseFallback
    || (titleContainsCjk(templateTitle) ? templateTitle : '');
  const templateTitleEnUsable = templateTitleEn
    && !titleContainsCjk(templateTitleEn)
    && isLikelyEnglishListingTitle(templateTitleEn)
    ? templateTitleEn
    : '';
  const templateTitleAsEn = !titleContainsCjk(templateTitle) && isLikelyEnglishListingTitle(templateTitle)
    ? templateTitle
    : '';
  const finalTitleEn = String(resolvedTitleEn || '').trim()
    || englishFallback
    || templateTitleEnUsable
    || templateTitleAsEn;

  const mainUrl = carouselUrlsForListingItem(listingItem)[0] || '';
  const detailUrls = detailImageUrlsForListingItem(listingItem);
  const printUrls = printImageUrlsForListingItem(listingItem);
  const initialPrintNames = printImageNamesForListingItem(listingItem);
  const printNames = await Promise.all(
    printUrls.map((url, index) => resolvePrintDisplayName(url, initialPrintNames[index] || '', index)),
  );
  const colorSlots = listingColorSlots(listingItem);
  const fallbackPatternName = readablePrintName(
    listingItem?.mainImageNames?.[0],
    listingItem?.mainImageName,
    fileNameFromUrl(mainUrl),
  );
  const slots = colorSlots.length > 0
    ? colorSlots
    : (mainUrl
      ? [{
        patternName: fallbackPatternName,
        mainName: fallbackPatternName,
        mainUrl,
        detailUrls,
        detailNames: [],
      }]
      : []);

  const templateSkcMediaByIndex = ensureSkcList(base).map(snapshotTemplateSkcMedia);
  const skcList = ensureSkcList(base).map((skc) => clearSkcPublishImages({ ...skc }));
  const filledIndexes = new Set();
  const usedIndexes = new Set();
  const hasTemplateMaterial = formHasTemuMaterialImage(base);
  /** @type {Promise<object|null>|null} */
  let materialFromMainPromise = null;
  /** @type {object|null} */
  let materialFromMain = null;

  const fillSkcMedia = async (skcIndex, slot, sortBase) => {
    if (skcIndex < 0 || skcIndex >= skcList.length) return;
    const skc = { ...(skcList[skcIndex] || {}) };
    // 无模板素材图时：用第一色商品库原主图并行裁 1:1 素材图，不阻塞主图转平台
    if (
      skcIndex === 0
      && !hasTemplateMaterial
      && !materialFromMainPromise
      && shopId
      && slot.mainUrl
    ) {
      const sourceUrl = toAbsoluteUrl(slot.mainUrl);
      materialFromMainPromise = deriveTemuMaterialFromMainSlot({
        shopId,
        mainSlot: {
          name: slot.mainName || slot.patternName || 'main.jpg',
          ipeUrl: sourceUrl,
          publicUrl: sourceUrl,
          previewUrl: sourceUrl,
          url: sourceUrl,
        },
      });
    }
    const mainImage = slot.mainUrl
      ? await imageFromUrl(slot.mainUrl, sortBase, slot.mainName || slot.patternName, convertOpts(1))
      : null;
    if (mainImage) {
      skc.mainImage = mainImage;
      skc.squareImage = null;
      skc.colorBlockImage = null;
    }
    const details = (
      await Promise.all((slot.detailUrls || []).slice(0, MAX_DETAIL_IMAGES).map((url, idx) => imageFromUrl(
        url,
        sortBase + idx + 2,
        slot.detailNames?.[idx] || '',
        convertOpts(2),
      )))
    ).filter(Boolean);
    skc.detailImages = details.slice(0, MAX_DETAIL_IMAGES);
    if (slot.colorCode) skc.colorCode = slot.colorCode;
    if (slot.colorLabel) {
      skc.colorLabel = slot.colorLabel;
    }
    const skuColorCode = extractColorCodeFromSupplierSku(
      (Array.isArray(skc.skuRows) ? skc.skuRows : [])[0]?.supplierSku,
    );
    if (!skc.colorCode && skuColorCode) {
      skc.colorCode = skuColorCode;
    }
    if (!skc.colorCode || !skc.colorLabel) {
      const fromNames = extractColorFromImageNames([
        slot.mainName || slot.patternName,
        ...((slot.detailUrls || []).map((url, idx) => (
          String(slot.detailNames?.[idx] || fileNameFromUrl(url) || '').trim()
        ))),
      ]);
      if (!skc.colorCode && fromNames.colorCode) skc.colorCode = fromNames.colorCode;
      if (!skc.colorLabel && fromNames.colorLabel) skc.colorLabel = fromNames.colorLabel;
    }
    // 商品库色槽优先；缺主图时用模板主图兜底，模板细节图接在创作图后
    const merged = await supplementSkcMediaFromTemplate(
      skc,
      templateSkcMediaByIndex[skcIndex],
      sortBase,
      convertOpts,
    );
    skcList[skcIndex] = appendPrintNamesToSkcSupplierCode(merged, printNames);
    if (skcHasPlatformPublishImages(skcList[skcIndex])) {
      filledIndexes.add(skcIndex);
      usedIndexes.add(skcIndex);
    }
  };

  for (let i = 0; i < slots.length; i += 1) {
    const slot = slots[i];
    if (!listingColorSlotHasPublishImages(slot)) continue;
    let skcIndex = resolveListingSlotSkcIndex(skcList, slot, { usedIndexes });
    // 无显式色槽时：仅单色模板可回退首个 SKC；多色必须命中颜色，禁止串色
    if (skcIndex < 0 && colorSlots.length === 0 && skcList.length === 1) skcIndex = 0;
    if (skcIndex >= 0 && usedIndexes.has(skcIndex)) {
      skcIndex = -1;
    }
    if (skcIndex < 0) {
      throw new Error(formatUnmatchedListingColorMessage(slot));
    }
    // eslint-disable-next-line no-await-in-loop
    await fillSkcMedia(skcIndex, slot, (i + 1) * 20);
  }

  if (materialFromMainPromise) {
    try {
      materialFromMain = await materialFromMainPromise;
    } catch (materialErr) {
      // 准备阶段还会再补一次；这里不阻断主图/细节图组包
      materialFromMain = null;
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[temu] parallel material crop failed, will retry on prepare:', materialErr?.message || materialErr);
      }
    }
  }

  const printImages = printUrls.map((url, idx) => printImageStub(
    url,
    printNames[idx] || '',
    idx,
  )).filter(Boolean);

  const nextSkcList = filterSkcsReadyForPlatformPublish(
    skcList.filter((_, index) => filledIndexes.has(index)),
  );

  let materialImage = base.materialImage || null;
  let materialImgUrl = String(base.materialImgUrl || base.materialImage?.temuUrl || '').trim();
  if (hasTemplateMaterial) {
    // 模板已有素材图：缺 temuUrl 时转传模板素材图本身
    if (!String(materialImage?.temuUrl || '').trim() && shopId) {
      try {
        materialImage = await deriveTemuMaterialFromMainSlot({
          shopId,
          mainSlot: materialImage,
        });
        materialImgUrl = String(materialImage?.temuUrl || '').trim();
      } catch {
        /* prepare 阶段再补 */
      }
    }
  } else if (materialFromMain) {
    materialImage = materialFromMain;
    materialImgUrl = String(materialFromMain.temuUrl || '').trim();
  }

  let temuVideos = Array.isArray(base.temuVideos) ? base.temuVideos.filter(Boolean) : [];
  if (!temuVideos.length) {
    const listingVideo = getListingProductVideo(listingItem);
    if (listingVideo) {
      const listingVid = String(listingVideo.vid || '').trim();
      // 源视频地址：优先 ipe 私有地址，其次平台预览地址
      const listingSourceUrl = String(listingVideo.ipeUrl || listingVideo.url || listingVideo.videoUrl || '').trim();
      if (listingVid) {
        // 已有平台 vid：直接复用，无需重复转传
        temuVideos = [{
          vid: listingVid,
          videoUrl: listingVideo.videoUrl || '',
          coverUrl: listingVideo.coverUrl || '',
          ipeUrl: listingSourceUrl,
        }];
      } else if (shopId && listingSourceUrl) {
        // 仅有预览/私有地址、没有平台 vid：必须转传才能拿到可刊登的 vid
        try {
          const file = await videoSpaceRecordToUploadFile({
            id: listingVideo.videoSpaceId,
            name: listingVideo.name || 'video.mp4',
            outputVideoUrl: listingSourceUrl,
            _playUrl: listingSourceUrl,
          }, listingVideo.name || 'video.mp4');
          const res = await temuPlatformAPI.uploadProductVideo(shopId, file, {
            fromVideoSpace: Boolean(listingVideo.videoSpaceId),
          });
          const uploadedVid = String(res?.vid || '').trim();
          if (uploadedVid) {
            temuVideos = [{
              vid: uploadedVid,
              videoUrl: res.videoUrl || '',
              coverUrl: res.coverUrl || listingVideo.coverUrl || '',
              ipeUrl: res.ipeUrl || listingSourceUrl,
            }];
          } else {
            console.warn('[temu] 素材视频转传未取得平台 vid，已跳过主图视频', listingVideo);
          }
        } catch (err) {
          /* 视频可选：转换失败不阻断主图刊登 */
          console.warn('[temu] 素材视频转传失败，已跳过主图视频', err);
        }
      }
    }
  }

  return {
    ...base,
    title: finalTitle,
    // 同步清掉模板里误存的 productName（如纯数字 ID），避免组包 emptyPublishForm 再拾起来
    productName: finalTitle,
    titleEn: finalTitleEn,
    englishTitle: finalTitleEn,
    printImages,
    ipePrintImageNames: printNames,
    ipePrintInfringement: resolveListingPrintInfringementForTemu(listingItem),
    categoryId: categoryMeta.categoryId || base.categoryId || '',
    productTypeId: categoryMeta.productTypeId || base.productTypeId || '',
    categoryPathLabel: categoryMeta.categoryPathLabel || base.categoryPathLabel || '',
    skcList: nextSkcList,
    selectedSizeIds: nextSkcList.length
      ? deriveSelectedSizeIds(nextSkcList)
      : (Array.isArray(base.selectedSizeIds) ? base.selectedSizeIds : []),
    materialImage,
    materialImgUrl,
    temuVideos,
  };
}

/**
 * 保存/提交前：把印花图名称接到各 SKC 货号「-」后（多张用 +）；不改商家 SKU。
 * @param {object} form
 * @param {string[]} [printNames]
 */
export function applyPrintNamesToTemuForm(form, printNames) {
  const fromImages = printNamesFromTemuPrintImages(form?.printImages);
  const fallbackNames = Array.isArray(form?.ipePrintImageNames)
    ? form.ipePrintImageNames.map((n) => readablePrintName(n)).filter(Boolean)
    : [];
  const names = Array.isArray(printNames) && printNames.length
    ? printNames.map((n) => readablePrintName(n) || String(n || '').trim()).filter(Boolean)
    : (fromImages.length ? fromImages : fallbackNames);
  if (!form || !names.length) return form;
  const skcList = Array.isArray(form.skcList)
    ? form.skcList.map((skc) => ({
      ...skc,
      supplierCode: appendPrintNamesAfterHyphen(
        skc?.supplierCode,
        names,
        { maxLen: TEMU_SUPPLIER_CODE_MAX_LEN },
      ),
    }))
    : form.skcList;
  return {
    ...form,
    ipePrintImageNames: names,
    skcList,
  };
}
